import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserApiService } from '../../../core/services/user-api.service';
import { Router } from '@angular/router';
import { CustomDatePipe } from '../../../core/pipes/custom-date.pipe';
import { CompanyCodeService } from '../../../core/services/adminService/company-code.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { BuyerServiceService } from '../../../core/services/buyers/buyer-service.service';
import { UserData } from '../../../core/interfaces/user';
import { SessionStorageService } from '../../../core/services/session-storage';
import { HttpParams } from '@angular/common/http';
import { IBuyerRequest } from '../../../core/interfaces/buyer';
import { CommonService } from '../../../core/services/common.service';
import { constantTxt } from '../../../core/constants/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-buyer-manage-request-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomDatePipe,
    NzButtonModule,
    NzSelectModule,
    NzInputModule,
    NzTableModule,
  ],
  templateUrl: './buyer-manage-request-form.component.html',
  styleUrls: ['./buyer-manage-request-form.component.less'],
})
export class BuyerManageRequestFormComponent implements OnInit, OnDestroy {
  isViewMode: boolean = false;
  manageForm!: FormGroup;
  requests: IBuyerRequest[] = [];
  companies: any[] = [];
  buyers: any[] = [];
  purchasingOffices: any[] = [];
  statuses = [
    'ALL',
    'DRAFT',
    'OPEN',
    'IN PROGRESS',
    'PARTIAL QUOTED',
    'CLOSED',
    'CANCELED',
  ];
  itemNo: number = 1;
  userFormAddItemService: any;
  attachmentMap: { [key: string]: boolean } = {};
  userData!: UserData;
  showViewItem: boolean = false;
  noDataAvailable = false;
  pageNumber = 1;
  pageSize = 100;
  selectedRequests: Set<number> = new Set(); // Store selected request IDs
  customNoDataMessage = constantTxt.noDataFound;
  companiesSubscription!: Subscription;
  getOfficesListSubscription!: Subscription;
  getBuyersSubscription!: Subscription;
  getAssignedPNARequestsSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userApi: UserApiService,
    private readonly router: Router,
    private readonly companyCodeService: CompanyCodeService,
    private readonly buyerServiceService: BuyerServiceService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly commonService: CommonService
  ) {
    this.userData = JSON.parse(
      this.sessionStorageService.getItem('loggedInUser')
    );
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadApiData();
  }

  initializeForm() {
    this.manageForm = this.fb.group({
      companyCode: ['', Validators.required],
      buyer: ['', Validators.required],
      purchasingOffice: ['', Validators.required],
      status: ['IN PROGRESS'],
      pnaNo: ['', [Validators.pattern('^\\d+(?:-\\d+)?$')]],
    });
  }

  loadApiData() {
    this.loadDropdowns();
    this.fetchOurchasingOffice();
    this.loadBuyerPna();
    this.manageRequestControls();
  }

  loadDropdowns() {
    this.companiesSubscription = this.companyCodeService
      .getCompanies()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.companies = data;
          }
        },
        error: () => {
          this.companies = [];
        },
      });

    this.getBuyersSubscription = this.userApi.getBuyers().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.buyers = data;
        }
      },
      error: () => {
        this.buyers = [];
      },
    });

    this.getOfficesListSubscription = this.userApi.getOfficesList().subscribe({
      next: (res) => {
        let data = res.body;
        if (data) {
          this.purchasingOffices = data;
        }
      },
      error: () => {
        this.purchasingOffices = [];
      },
    });
  }

  manageRequestControls() {
    this.manageForm.controls['purchasingOffice'].disable();
  }

  fetchOurchasingOffice(): void {
    const po = this.userData?.userPurchasingOffice;
    if (po) {
      this.manageForm.controls['purchasingOffice'].setValue(po);
    }
  }

  loadBuyerPna(): void {
    const filter = this.manageForm.getRawValue();
    filter.buyer = this.userData.userId;

    let params = new HttpParams();
    if (filter.buyer) params = params.set('buyerId', filter.buyer);
    if (filter.companyCode) params = params.set('ccn', filter.companyCode);
    if (filter.purchasingOffice)
      params = params.set('purchasingOffice', filter.purchasingOffice);
    if (filter.status) params = params.set('status', filter.status);

    if (filter.pnaNo) {
      const { pnaNo, PnaYear } = this.commonService.extractPNA(
        filter.pnaNo || ''
      );
      params = params.set('pnaNo', pnaNo || '');
      params = params.set('year', PnaYear);
    }

    this.getAssignedPNARequestsSubscription = this.buyerServiceService
      .getAssignedPNARequests(params)
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.requests = data;
          } else {
            this.requests = [];
          }
        },
        error: () => {
          alert('Something went wrong. Please try again later.');
        },
      });
  }

  // Navigate to view request with clean URL - all data passed through state
  navigateToView(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'view',
      pnaNo: pnaNo,
      year: pnaYear,
      role: 'buyer',
      returnUrl: '/buyer/request',
    };

    this.router.navigate(['/buyer/view-request'], {
      state: navigationState,
    });
  }

  // Navigate to edit request with clean URL - all data passed through state
  navigateToEdit(pnaNo: string, pnaYear: string): void {
    const navigationState = {
      mode: 'edit',
      pnaNo: pnaNo,
      year: pnaYear,
      role: 'buyer',
      returnUrl: '/buyer/request',
    };

    this.router.navigate(['/buyer/edit-request'], {
      state: navigationState,
    });
  }

  getStatusFlags(status: string | undefined | null) {
    const s = (status || '').toLowerCase();
    return {
      isDraft: s === 'draft',
      isOpen: s === 'open',
      isDeleted: s === 'deleted',
      isClosed: s === 'closed',
      inProgress: s === 'in progress',
      isPartiallyQuoted: s === 'partial quoted',
    };
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageNumber = 1;
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageNumber = pageIndex;
  }

  ngOnDestroy(): void {
    if (this.companiesSubscription) {
      this.companiesSubscription.unsubscribe();
    }
    if (this.getBuyersSubscription) {
      this.getBuyersSubscription.unsubscribe();
    }
    if (this.getOfficesListSubscription) {
      this.getOfficesListSubscription.unsubscribe();
    }
    if (this.getAssignedPNARequestsSubscription) {
      this.getAssignedPNARequestsSubscription.unsubscribe();
    }
  }
}
